import {useTranslation} from "react-i18next";
import {useLocation, useRouteMatch} from "react-router-dom";
import {onDesktop, p} from "@pg-design/helpers-css";
import {i18n as Ii18n} from "i18next";

import {CitySlug} from "../../../../../config/cities";
import {dataLayer} from "../../../../../utils/data_layer";
import {capitalize} from "../../../../../utils/misc";
import {chartsRoutes, checkIfIsChartsCityView, checkIfIsDetailedView} from "../../../common/app/routing/charts_routes";
import {mapOfferTypeToValidPath, mapPathnameToOfferType} from "../../../common/app/routing/offer_type";
import {useSelectedRegionDisplay} from "../../../features/analytical_platform/hooks/use_selected_region_display";
import {Breadcrumbs, IBreadcrumb} from "../../breadcrumbs/Breadcrumbs";
import {BreadcrumbsItem} from "../../breadcrumbs/BreadcrumbsItem";

export const ChartsBreadcrumbs = () => {
    const {t, i18n} = useTranslation();
    const {path} = useRouteMatch();
    const {pathname} = useLocation();
    const {citySlug, isKnownCity, regionLabel} = useSelectedRegionDisplay();
    const offerType = mapPathnameToOfferType(pathname);
    const handleDataLayerEvent = (button: string) => {
        dataLayer({
            event: "top_menu_click",
            button
        });
    };

    const detailedViewTitle = mapPathnameToDetailedViewTitle(path, i18n);
    const isDetailedView = checkIfIsDetailedView(pathname);
    const isChartsCityView = checkIfIsChartsCityView(pathname, i18n.language);
    const platformTitle = t("common.analytical_platform");

    const generateChartsBreadcrumb = () => {
        const platformBreadcrumb: IBreadcrumb = {
            title: platformTitle,
            children: platformTitle,
            to: mapOfferTypeToValidPath(offerType, chartsRoutes[i18n.language].properties.root, i18n),
            onClick: () => handleDataLayerEvent(platformTitle)
        };
        const chartsBreadcrumbs: IBreadcrumb[] = [platformBreadcrumb];

        if (isChartsCityView) {
            const cityBreadcrumbLabel = !isKnownCity
                ? capitalize(regionLabel)
                : citySlug !== CitySlug.KATOWICE
                  ? capitalize(t(`city.${citySlug}.nominative`))
                  : `Katowice / ${
                        i18n.language === "pl" ? "Górnośląsko-Zagłębiowska Metropolia" : "Upper Silesia Metropolis"
                    }`;
            const cityBreadcrumb: IBreadcrumb = {
                title: cityBreadcrumbLabel,
                children: cityBreadcrumbLabel,
                to: mapOfferTypeToValidPath(offerType, chartsRoutes[i18n.language].properties.city.root, i18n),
                onClick: () => handleDataLayerEvent(cityBreadcrumbLabel)
            };
            chartsBreadcrumbs.push(cityBreadcrumb);
        }

        if (isChartsCityView && isDetailedView && detailedViewTitle) {
            const detailBreadcrumb = {
                title: detailedViewTitle,
                children: detailedViewTitle,
                to: "#",
                onClick: () => handleDataLayerEvent(detailedViewTitle)
            };
            chartsBreadcrumbs.push(detailBreadcrumb as IBreadcrumb);
        }

        if (pathname === chartsRoutes[i18n.language].flats.fltr) {
            const fltrBreadcrumb = {
                title: t("nav.fltr_dashboards"),
                children: t("nav.fltr_dashboards"),
                to: "#",
                onClick: () => handleDataLayerEvent("FLTR")
            };
            chartsBreadcrumbs.push(fltrBreadcrumb);
        }

        return [...chartsBreadcrumbs];
    };

    const breadcrumbsData = generateChartsBreadcrumb();

    return (
        <Breadcrumbs
            css={[p(1.5, 1, 0, 1), onDesktop(p(2, 3, 0, 3))]}
            homeTo={chartsRoutes[i18n.language].root}
            homeTitle="BIG DATA RynekPierwotny.pl"
        >
            {breadcrumbsData.map((data, i) => {
                const isLastItem = breadcrumbsData.length === i + 1;
                return (
                    <BreadcrumbsItem
                        title={data.title}
                        to={data.to}
                        isLinkingToCurrentLocation={isLastItem}
                        onClick={data.onClick}
                        key={i}
                        position={(i + 2).toString()}
                    >
                        {data.children}
                    </BreadcrumbsItem>
                );
            })}
        </Breadcrumbs>
    );
};

const mapPathnameToDetailedViewTitle = (pathname: string, i18n: Ii18n): string | undefined | null => {
    switch (pathname) {
        case chartsRoutes[i18n.language].properties.city.locationSummary:
        case chartsRoutes[i18n.language].flats.city.locationSummary:
        case chartsRoutes[i18n.language].houses.city.locationSummary:
            return i18n.t("nav.location_summary");
        case chartsRoutes[i18n.language].properties.city.prices:
        case chartsRoutes[i18n.language].flats.city.prices:
            return i18n.t("nav.prices");
        case chartsRoutes[i18n.language].properties.city.offer:
        case chartsRoutes[i18n.language].flats.city.offer:
            return i18n.t("nav.offer");
        case chartsRoutes[i18n.language].properties.city.demand:
        case chartsRoutes[i18n.language].flats.city.demand:
            return i18n.t("nav.demand");
        case chartsRoutes[i18n.language].properties.city.sold:
        case chartsRoutes[i18n.language].flats.city.sold:
            return i18n.t("nav.sold");
        case chartsRoutes[i18n.language].properties.city.added:
        case chartsRoutes[i18n.language].flats.city.added:
            return i18n.t("nav.added");
        default:
            return undefined;
    }
};
